import { AxiosError } from 'axios';
import { AxiosService } from '../../axios/axios.service';

import { LLog } from 'src/lib/logging/LLog';
import { LOG_COMPONENTS, LOG_OPERATIONS } from 'src/lib/logging/LLog.constants';
import {
  tPlan,
  tPlansResponse,
} from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.types';
import {
  BillingError,
  tAddon,
  tCancelSubscriptionPayload,
  tConvertTrialToSubscriptionResponse,
  tConvertTrialToSubscriptionResponseData,
  tCreateSubscriptionPayload,
  tCreateSubscriptionRequest,
  tCreateSubscriptionResponse,
  tCreateSubscriptionResponseData,
  tLeadsalesBFFResponse,
  tLeadsalesBillingResponse,
  tLeadsalesResponse,
  tSetupAvailability,
  tSubscriptionFeatures,
  tSubscriptionSummary,
  tValidateCouponResponse,
} from './Subscription.types';

const SubscriptionService = {
  /**
   * This function is used to create a subscription
   */
  validateCoupon: async function (
    wid: string,
    couponCode: string,
    signal?: AbortSignal,
  ): Promise<tValidateCouponResponse> {
    const url = `/workspace/${wid}/coupons/${encodeURIComponent(couponCode)}/validate`;
    const config = { isBFFService: true, headers: {}, signal };
    try {
      return await AxiosService.get<tValidateCouponResponse>(url, config);
    } catch (e) {
      const error = e as AxiosError<tValidateCouponResponse>;
      return Promise.reject(error.response?.data ?? { valid: false, message: 'Error validating coupon' });
    }
  },

  createSubscription: async function (
    payload: tCreateSubscriptionPayload,
  ): Promise<tCreateSubscriptionResponseData> {
    const { productId, extraSeats, paymentId, wid, isTrial, couponCode, extraProducts } =
      payload;

    const url = `/workspace/${wid}/tiers/subscription`;

    const body = {
      product_id: productId,
      extra_seats: extraSeats,
      payment_id: paymentId,
      ...(isTrial && { trial: true }),
      ...(couponCode && { coupon_code: couponCode }),
      ...(extraProducts.length > 0 && { extra_products: extraProducts }),
    };

    const config = { isBFFService: true, headers: {} };

    try {
      const response = await AxiosService.post<
        tCreateSubscriptionRequest,
        tCreateSubscriptionResponse
      >(url, body, config);
      return response.data;
    } catch (e) {
      const error = e as AxiosError<BillingError>;
      return Promise.reject(error.response?.data);
    }
  },
  /**
   * This function is used to read billing notifications
   */
  readNotification: async function (
    workspaceId: string,
    notificationId: string,
  ): Promise<void> {
    const url = `/workspace/${workspaceId}/notifications/${notificationId}/read`;
    const config = { isBFFService: true, headers: {} };
    try {
      await AxiosService.put<void, void>(url, undefined, config);
      return Promise.resolve();
    } catch (e) {
      const error = e as AxiosError<BillingError>;
      return Promise.reject(error.response?.data);
    }
  },

  confirmTrialSubscription: async function (
    workspaceId: string,
    payment_method_id: string,
  ): Promise<void> {
    try {
      const url = `/workspace/${workspaceId}/subscription/confirm-trial-subscription`;
      const payload = {
        payment_method_id: payment_method_id,
      };
      const config = { isBFFService: true, headers: {} };
      await AxiosService.post(url, payload, config);
      return Promise.resolve();
    } catch (e) {
      const error = e as AxiosError<BillingError>;
      return Promise.reject(error.response?.data);
    }
  },

  cancelTrialSubscription: async function (
    workspaceId: string,
    cancellation_reason: string,
  ): Promise<void> {
    const url = `/workspace/${workspaceId}/subscription/cancel-trial-prepayment`;
    const payload = {
      cancellation_reason: cancellation_reason,
    };
    const config = { isBFFService: true, headers: {} };
    try {
      await AxiosService.post(url, payload, config);
      return Promise.resolve();
    } catch (e) {
      const error = e as AxiosError<BillingError>;
      return Promise.reject(error.response?.data);
    }
  },

  /**
   * This function is used to get the current subscription summary
   */
  getSummary: async function (
    workspaceId: string,
  ): Promise<tSubscriptionSummary> {
    const url = `/workspace/${workspaceId}/subscription`;
    // Rejects with the raw AxiosError (like getFeatures): the hook's retry
    // needs response.status and config.authDegraded, and a bodyless 401 used
    // to reject `undefined` here — which blew up the hook's own catch (BUG-868).
    const response =
      await AxiosService.get<tLeadsalesBFFResponse<tSubscriptionSummary>>(url);

    return response.data;
  },

  convertTrialToSubscription: async function (
    workspaceId: string,
    paymentMethodId: string,
  ): Promise<tConvertTrialToSubscriptionResponseData> {
    try {
      const url = `/workspace/${workspaceId}/subscription/convert-trial`;
      const config = { headers: {}, isBFFService: true };
      const payload = {
        payment_method_id: paymentMethodId,
      };
      const response = await AxiosService.post<
        { payment_method_id: string },
        tConvertTrialToSubscriptionResponse
      >(url, payload, config);
      return Promise.resolve(response.data);
    } catch (e) {
      const error = e as AxiosError<BillingError>;
      return Promise.reject(error.response?.data);
    }
  },

  getSetupAvailability: async function (
    workspaceId: string,
  ): Promise<tSetupAvailability> {
    const url = `/workspace/${workspaceId}/offer-available`;
    const config = { isBFFService: true, headers: {} };
    try {
      const setupAvailabilityResponse = await AxiosService.get<
        tLeadsalesResponse<tSetupAvailability>
      >(url, config);
      return setupAvailabilityResponse.data;
    } catch (e) {
      const error = e as AxiosError<BillingError>;
      throw error.response?.data;
    }
  },

  /**
   * Fetches the available plans for a workspace and billing period. The billing
   * currency is resolved server-side by the BFF (RET-1244) from the
   * CloudFront-Viewer-Country edge header and the workspace's existing
   * subscription, so it is no longer sent from the client; the returned plans
   * carry their own currency.
   */
  getAvailablePlans: async function (
    workspaceId: string,
    period: string,
  ): Promise<tPlan[]> {
    const url = `/workspace/${workspaceId}/plans?frequency=${period}`;
    try {
      const plansGetResponse = await AxiosService.get<tPlansResponse>(url);
      return Promise.resolve(plansGetResponse.data);
    } catch (e) {
      const error = e as AxiosError<BillingError>;
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'SubscriptionManager.service',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error in SubscriptionManager.service',
        error as unknown as Error,
      );
      return Promise.reject(error);
    }
  },

  cancelSubscription: async function (
    workspaceId: string,
    question1: string,
    question2: string,
    requestedBy: string,
  ): Promise<void> {
    const config = { isBFFService: true, headers: {} };

    const url = `/workspace/${workspaceId}/subscription/schedule-cancelation`;

    const payload: tCancelSubscriptionPayload = {
      form_content: {
        0: question1,
        1: question2,
      },
      requested_by: requestedBy,
      requested_from: 'LEADSALES',
    };
    // Resolves only once leadbilling confirms the cancellation is scheduled, and
    // rejects otherwise, so no caller can announce a cancellation that did not
    // happen. The rejection is the relayed refusal body (its `code` is what a
    // caller localizes), or undefined when the request got no response.
    let response: tLeadsalesBillingResponse<unknown>;
    try {
      response = await AxiosService.put<
        tCancelSubscriptionPayload,
        tLeadsalesBillingResponse<unknown>
      >(url, payload, config);
    } catch (e) {
      const error = e as AxiosError<BillingError>;
      return Promise.reject(error.response?.data);
    }

    // AxiosService resolves the response BODY, so the HTTP status is not here to
    // read; a 2xx body that does not say success is not a scheduled cancellation.
    if (response?.success !== true) {
      return Promise.reject(response);
    }
  },

  getAddon: async function (
    workspaceId: string,
    addonId: string,
  ): Promise<tAddon> {
    const config = { isBFFService: true, headers: {} };
    const url = `/workspace/${workspaceId}/addons/${addonId}`;
    try {
      const addonResponse = await AxiosService.get<
        tLeadsalesBillingResponse<tAddon>
      >(url, config);
      return Promise.resolve(addonResponse.data);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  getFeatures: async function (
    workspaceId: string,
  ): Promise<tSubscriptionFeatures> {
    try {
      const response = await AxiosService.get<
        tLeadsalesBillingResponse<tSubscriptionFeatures>
      >(`/workspace/${workspaceId}/tiers/features`);

      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  },
};

export default SubscriptionService;
